// Services dos processadores do merchant (superAdmin) — molde
// services/admin/merchant-mtls-certificates.ts: queryOptions exportada p/ teste,
// invalidação por predicate e toasts com getApiErrorMessage.
//
// O processador é a CREDENCIAL, não o meio de pagamento: cadastrado uma vez e
// apontado por quantos meios forem necessários, inclusive de white labels
// diferentes.
import { api } from "@/api"
import { BASE_URL, ENDPOINTS } from "@/constants"
import { toast } from "@/hooks/use-toast"
import { getApiErrorMessage } from "@/lib/api-error"
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@/libs"
import type {
  MerchantProcessor,
  MerchantProcessorCreatePayload,
  MerchantProcessorUpdatePayload,
} from "@/types/admin-merchant-processor"
import type { ProcessorPaymentMethod } from "@/types/admin-processor-config"

import { ADMIN_PROCESSOR_CONFIG_QUERY_KEY } from "./processor-config"

export const ADMIN_MERCHANT_PROCESSORS_QUERY_KEY =
  "admin-merchant-processors"

// Nenhuma URL leva o merchant: o gateway resolve pelo token. Mandar daqui
// deixaria o cliente escolher de quem são as credenciais.
const listUrl = () =>
  `${BASE_URL}${ENDPOINTS.ADMIN_MERCHANT_PROCESSORS}`

const processorUrl = (processorId: string) =>
  `${BASE_URL}${ENDPOINTS.ADMIN_MERCHANT_PROCESSOR.replace("{{processorId}}", processorId)}`

const testConnectionUrl = (processorId: string) =>
  `${BASE_URL}${ENDPOINTS.ADMIN_MERCHANT_PROCESSOR_TEST_CONNECTION.replace("{{processorId}}", processorId)}`

const eligibleUrl = (whiteLabelId: string) =>
  `${BASE_URL}${ENDPOINTS.ADMIN_PROCESSOR_CONFIG_ELIGIBLE_PROCESSORS.replace(
    "{{whiteLabelId}}",
    whiteLabelId
  )}`

export const merchantProcessorsQueryOptions = () => ({
  queryKey: [ADMIN_MERCHANT_PROCESSORS_QUERY_KEY],
  queryFn: async (): Promise<MerchantProcessor[]> => {
    const { data } =
      await api.get<MerchantProcessor[]>(listUrl())
    return data
  },
})

export const useMerchantProcessors = () =>
  useQuery(merchantProcessorsQueryOptions())

/**
 * Um processador do merchant.
 *
 * Busca o detalhe, não a lista: só o detalhe traz `linkedConfigs` — QUAIS meios de pagamento
 * dependem da credencial. A lista não os traz para não pagar a varredura por linha.
 */
export const merchantProcessorQueryOptions = (
  processorId: string
) => ({
  queryKey: [
    ADMIN_MERCHANT_PROCESSORS_QUERY_KEY,
    processorId,
  ],
  queryFn: async (): Promise<MerchantProcessor> => {
    const { data } = await api.get<MerchantProcessor>(
      processorUrl(processorId)
    )
    return data
  },
  enabled: Boolean(processorId),
})

export const useMerchantProcessor = (processorId: string) =>
  useQuery(merchantProcessorQueryOptions(processorId))

/**
 * Processadores que podem atender uma configuração do white label.
 *
 * São os do MERCHANT do white label, não os do usuário logado: um superAdmin administra white
 * labels de vários merchants, e oferecer a lista errada faz o save recusar com 404.
 */
export const eligibleProcessorsQueryOptions = (
  whiteLabelId: string,
  method?: ProcessorPaymentMethod,
  enabled = true
) => ({
  queryKey: [
    ADMIN_MERCHANT_PROCESSORS_QUERY_KEY,
    "eligible",
    whiteLabelId,
    method ?? null,
  ],
  queryFn: async (): Promise<MerchantProcessor[]> => {
    const { data } = await api.get<MerchantProcessor[]>(
      eligibleUrl(whiteLabelId),
      { params: method ? { method } : undefined }
    )
    return data
  },
  enabled: enabled && Boolean(whiteLabelId),
})

/**
 * @param enabled adia a busca — a listagem tem uma linha por método, e buscar os
 * elegíveis de todas de uma vez seria uma requisição por linha para dados que o
 * operador só vê ao abrir um combo.
 */
export const useEligibleProcessors = (
  whiteLabelId: string,
  method?: ProcessorPaymentMethod,
  enabled = true
) =>
  useQuery(
    eligibleProcessorsQueryOptions(
      whiteLabelId,
      method,
      enabled
    )
  )

const invalidateProcessors = (
  queryClient: ReturnType<typeof useQueryClient>
) =>
  queryClient.invalidateQueries({
    predicate: (query) =>
      query.queryKey[0] ===
      ADMIN_MERCHANT_PROCESSORS_QUERY_KEY,
  })

export const useCreateMerchantProcessor = (
  onSuccess?: (processor: MerchantProcessor) => void
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (
      payload: MerchantProcessorCreatePayload
    ) => {
      const { data } = await api.post<MerchantProcessor>(
        listUrl(),
        payload
      )
      return data
    },
    onSuccess: (processor) => {
      invalidateProcessors(queryClient)
      onSuccess?.(processor)
    },
    onError: (error: unknown) => {
      toast({
        variant: "destructive",
        title: getApiErrorMessage(
          error,
          "Não foi possível cadastrar o processador."
        ),
      })
    },
  })
}

export const useUpdateMerchantProcessor = (
  processorId: string,
  onSuccess?: () => void
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (
      payload: MerchantProcessorUpdatePayload
    ) => {
      const { data } = await api.patch<MerchantProcessor>(
        processorUrl(processorId),
        payload
      )
      return data
    },
    onSuccess: () => {
      invalidateProcessors(queryClient)
      onSuccess?.()
    },
    onError: (error: unknown) => {
      toast({
        variant: "destructive",
        title: getApiErrorMessage(
          error,
          "Não foi possível salvar o processador."
        ),
      })
    },
  })
}

export const useDeleteMerchantProcessor = (
  onSuccess?: () => void
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (processorId: string) => {
      await api.delete(processorUrl(processorId))
    },
    onSuccess: () => {
      invalidateProcessors(queryClient)
      onSuccess?.()
    },
    onError: (error: unknown) => {
      // 409 é o caso esperado: apagar um processador em uso deixaria meios de
      // pagamento órfãos. A mensagem do backend diz QUANTOS dependem dele,
      // então ela vale mais do que um texto genérico daqui.
      toast({
        variant: "destructive",
        title: getApiErrorMessage(
          error,
          "Não foi possível remover o processador."
        ),
      })
    },
  })
}

export interface ProcessorTestConnectionResult {
  success: boolean
  latencyMs?: number | null
  detail?: string | null
}

/**
 * Testa a conexão real com o processador.
 *
 * Mora aqui, e não no meio de pagamento: quem tem credencial é o processador, e testá-la uma vez
 * vale para todos os meios que a usam.
 */
export const useTestMerchantProcessorConnection = () =>
  useMutation({
    mutationFn: async (processorId: string) => {
      const { data } =
        await api.post<ProcessorTestConnectionResult>(
          testConnectionUrl(processorId)
        )
      return data
    },
    onError: (error: unknown) => {
      toast({
        variant: "destructive",
        title: getApiErrorMessage(
          error,
          "Não foi possível testar a conexão."
        ),
      })
    },
  })

export interface ChangeConfigFallbackPayload {
  configId: string
  /**
   * Credencial de contingência, ou `null` para espelhar a que processa.
   *
   * "Sem contingência" não é um estado possível — a coluna é `NOT NULL` e sempre apontou para
   * alguém. `null` é a forma explícita de dizer "a mesma do principal"; string vazia o backend
   * recusa com 400, justamente para campo não preenchido não virar decisão do operador.
   */
  processorId: string | null
}

export interface ChangeConfigProcessorPayload {
  configId: string
  processorId: string
  /** Move junto a contingência, quando ela aponta para o processador atual. */
  moveFallback?: boolean
}

/**
 * Escolhe a credencial de CONTINGÊNCIA da configuração.
 *
 * Decisão separada da troca de processador de propósito: mover quem processa muda por onde o
 * dinheiro sai; mudar a contingência muda só para onde ele vai quando a primeira tentativa falha.
 */
export const useChangeConfigFallback = (
  onSuccess?: () => void
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      configId,
      processorId,
    }: ChangeConfigFallbackPayload) => {
      const url = `${BASE_URL}${ENDPOINTS.ADMIN_PROCESSOR_CONFIG_CHANGE_FALLBACK.replace("{{uuid}}", configId)}`
      // O corpo é obrigatório mesmo quando a escolha é "a mesma do principal": PATCH sem corpo
      // resetaria a contingência em silêncio e ainda registraria evento na trilha.
      const { data } = await api.patch(url, { processorId })
      return data
    },
    onSuccess: () => {
      invalidateProcessors(queryClient)
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] ===
          ADMIN_PROCESSOR_CONFIG_QUERY_KEY,
      })
      toast({
        title: "Contingência atualizada.",
        variant: "success",
      })
      onSuccess?.()
    },
    onError: (error: unknown) => {
      toast({
        variant: "destructive",
        title: getApiErrorMessage(
          error,
          "Não foi possível alterar a contingência."
        ),
      })
    },
  })
}

/**
 * Aponta uma configuração para outro processador do mesmo merchant.
 *
 * É o que consolida credenciais duplicadas: movidas todas as configurações para uma credencial,
 * as outras ficam sem vínculo e podem ser apagadas.
 */
export const useChangeConfigProcessor = (
  onSuccess?: () => void
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      configId,
      processorId,
      moveFallback,
    }: ChangeConfigProcessorPayload) => {
      const url = `${BASE_URL}${ENDPOINTS.ADMIN_PROCESSOR_CONFIG_CHANGE_PROCESSOR.replace("{{uuid}}", configId)}`
      const { data } = await api.patch(url, {
        processorId,
        moveFallback,
      })
      return data
    },
    onSuccess: () => {
      // Invalida os DOIS lados: o processador de origem perdeu um vínculo e o de
      // destino ganhou — mostrar a contagem velha faria o operador apagar o errado.
      invalidateProcessors(queryClient)
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] ===
          ADMIN_PROCESSOR_CONFIG_QUERY_KEY,
      })
      toast({
        title: "Meio de pagamento movido.",
        variant: "success",
      })
      onSuccess?.()
    },
    onError: (error: unknown) => {
      toast({
        variant: "destructive",
        title: getApiErrorMessage(
          error,
          "Não foi possível mover o meio de pagamento."
        ),
      })
    },
  })
}
